import * as React from "react";
import { Box, Chip, Typography } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Localized, useLocalization } from "@fluent/react";

import { urgencyAppearance } from "../utils/taskUrgency";
import type { TaskUrgency } from "../utils/taskUrgency";

type TaskDeadlineProps = {
    deadline: TaskUrgency;
    color?: string;
};

export const TaskDeadline: React.FC<TaskDeadlineProps> = ({
    deadline,
    color = "text.secondary",
}) => {
    const { l10n } = useLocalization();
    const appearance = urgencyAppearance[deadline.key];
    const isOverdue = deadline.key === "overdue";

    return (
        <Box
            sx={{
                minWidth: 0,
                display: "flex",
                flexDirection: {
                    xs: "row",
                    sm: "column",
                },
                alignItems: {
                    xs: "center",
                    sm: "flex-end",
                },
                flexWrap: "wrap",
                gap: 1,
                color,
            }}
        >
            <Chip
                size="small"
                icon={
                    isOverdue ? (
                        <ErrorOutlineIcon />
                    ) : deadline.dateLabel ? (
                        <AccessTimeIcon />
                    ) : undefined
                }
                label={l10n.getString(deadline.labelId, deadline.labelVars, deadline.label)}
                sx={(theme) => ({
                    ...(theme.palette.mode === "dark"
                        ? appearance.dark
                        : appearance.light),

                    border: "none",
                    borderRadius: "5px",
                    height: "auto",
                    minHeight: 30,
                    maxWidth: "100%",
                    fontSize: "0.8125rem",
                    fontWeight: 700,

                    "& .MuiChip-icon": {
                        color: "inherit",
                        fontSize: 19,
                        ml: 1,
                    },

                    "& .MuiChip-label": {
                        px: 1.25,
                        py: 0.35,
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                    },
                })}
            />

            {deadline.dateLabel && (
                <Typography
                    component="div"
                    color="inherit"
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        flexWrap: "wrap",
                        columnGap: 0.75,
                        fontSize: "0.8125rem",
                    }}
                >
                    <Localized id="deadline">
                        <span>_Termin:</span>
                    </Localized>

                    <Box
                        component="span"
                        sx={{
                            fontSize: "0.8375rem",
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                            whiteSpace: "nowrap",

                            color: (theme) => {
                                if (!isOverdue)
                                    return "text.primary";

                                return theme.palette.mode === "dark"
                                    ? appearance.dark.color
                                    : appearance.light.color;
                            },
                        }}
                    >
                        {deadline.dateLabel}
                    </Box>
                </Typography>
            )}
        </Box>
    );
};